<template>
  <VaDropdown :offset="[13, 0]" class="notification-dropdown" stick-to-edges :close-on-content-click="false">
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <VaBadge v-if="chatStore.unreadCount > 0" overlap>
          <template #text>{{ chatStore.unreadCount > 9 ? '9+' : chatStore.unreadCount }}</template>
          <VaIconNotification class="notification-dropdown__icon" />
        </VaBadge>
        <VaIconNotification v-else class="notification-dropdown__icon" />
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[420px] sm:h-auto">
      <section class="sm:max-h-[400px] p-4 overflow-auto">
        <div v-if="chatStore.notifications.length === 0" class="no-notifications">
          <VaIcon name="notifications_none" size="large" color="secondary" />
          <p class="text-secondary">Aucune notification</p>
        </div>
        <VaList v-else class="space-y-1 mb-2">
          <template v-for="(notif, index) in visibleNotifications" :key="notif.id">
            <VaListItem
              class="text-base notification-item"
              :class="{ unread: !notif.read }"
              @click="handleNotificationClick(notif)"
            >
              <VaListItemSection icon class="mx-0 p-0">
                <VaIcon
                  :name="
                    notif.conversationName.includes('📧')
                      ? 'mail'
                      : notif.conversationName.includes('💸')
                        ? 'account_balance_wallet'
                        : 'chat'
                  "
                  :color="notif.read ? 'secondary' : 'primary'"
                />
              </VaListItemSection>
              <VaListItemSection>
                <div class="notification-content">
                  <div class="notification-header">
                    <strong>{{ notif.conversationName }}</strong>
                  </div>
                  <div class="notification-message">{{ notif.message }}</div>
                  <div class="notification-time">{{ formatTime(notif.timestamp) }}</div>
                </div>
              </VaListItemSection>
              <VaListItemSection icon class="mx-1">
                <VaButton
                  preset="plain"
                  size="small"
                  icon="close"
                  @click.stop="chatStore.clearNotification(notif.id)"
                />
              </VaListItemSection>
            </VaListItem>
            <VaListSeparator v-if="index !== visibleNotifications.length - 1" class="mx-3" />
          </template>
        </VaList>

        <div v-if="chatStore.notifications.length > 0" class="notification-actions">
          <VaButton
            v-if="chatStore.notifications.length > baseNumberOfVisibleNotifications"
            preset="secondary"
            size="small"
            class="w-full mb-2"
            @click="displayAllNotifications = !displayAllNotifications"
          >
            {{ displayAllNotifications ? 'Voir moins' : `Voir tout (${chatStore.notifications.length})` }}
          </VaButton>
          <VaButton preset="primary" size="small" class="w-full" @click="chatStore.markAllAsRead()">
            Tout marquer comme lu
          </VaButton>
        </div>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useChatNotificationsStore } from '../../../../stores/chat-notifications'
import VaIconNotification from '../../../icons/VaIconNotification.vue'

const router = useRouter()
const chatStore = useChatNotificationsStore()
const displayAllNotifications = ref(false)
const baseNumberOfVisibleNotifications = 5

const visibleNotifications = computed(() => {
  const notifs = chatStore.notifications
  return displayAllNotifications.value ? notifs : notifs.slice(0, baseNumberOfVisibleNotifications)
})

const formatTime = (date: Date) => {
  const now = new Date()
  const diff = now.getTime() - new Date(date).getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return "À l'instant"
  if (minutes < 60) return `Il y a ${minutes} min`
  if (hours < 24) return `Il y a ${hours}h`
  if (days < 7) return `Il y a ${days}j`

  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
  })
}

const handleNotificationClick = async (notif: any) => {
  await chatStore.markAsRead(notif.id)

  // Si c'est une notification d'email (nom commence par 📧)
  if (notif.conversationName.includes('📧')) {
    router.push('/communications/mailbox')
  }
  // Si c'est une notification de transfert (nom commence par 💸)
  else if (notif.conversationName.includes('💸')) {
    // Supprimer la notification de transfert
    chatStore.removeTransferNotification(notif.conversationId)
    router.push('/admin/caisse/validation-transfert')
  } else {
    // Sinon, c'est une notification de chat
    router.push({
      path: '/communications/chatbox',
      query: { conversationId: notif.conversationId },
    })
  }
}

onMounted(async () => {
  // Charger les paramètres du chat
  await chatStore.loadChatSettings()

  // Démarrer le polling des notifications (seulement s'il n'est pas déjà démarré)
  chatStore.startPolling()
})

// Ne PAS arrêter le polling au unmount car la navbar reste active
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}

.no-notifications {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;

  p {
    margin-top: 0.5rem;
    font-size: 0.9rem;
  }
}

.notification-item {
  cursor: pointer;
  border-radius: 0.5rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--va-background-element);
  }

  &.unread {
    background-color: var(--va-background-element);

    .notification-content {
      font-weight: 500;
    }
  }
}

.notification-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  .notification-header {
    font-size: 0.9rem;
    color: var(--va-text-primary);
  }

  .notification-message {
    font-size: 0.85rem;
    color: var(--va-text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 250px;
  }

  .notification-time {
    font-size: 0.75rem;
    color: var(--va-text-secondary);
  }
}

.notification-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
</style>
